import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { CreateSubFolderParameters } from '../../../../../../../typings/api/skymap/rest/v1/folder';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { RequestParamsToApiPaths } from '../../../../../utils/typescript-utils';
import { useErrorHandling } from '../../../../hooks/use-error-handling';
import { useFocusElement } from '../../../../hooks/use-focus-element';
import { ProjectContext } from '../../../../state/project-state/project-state';
import { Button } from '../../../button/button';
import { Dialog } from '../../../dialog/dialog';
import { TextBox } from '../../../text-box/text-box';
import { TreeContext } from '../../state/folder-tree-state';

interface Props {
  onClose: (canceled: boolean) => void;
}

enum Status {
  Idle,
  Saving,
  Error,
}

const CreateFolderDialog = (props: Props) => {
  const { t } = useTranslation();
  const ref = useFocusElement<HTMLInputElement>();
  const projectContext = React.useContext(ProjectContext);
  const { selectedFolderId } = React.useContext(TreeContext);

  const { handleError, clearErrorForPath, getErrorForPath } =
    useErrorHandling<RequestParamsToApiPaths<CreateSubFolderParameters>>();
  const [status, setStatus] = React.useState(Status.Idle);
  const [name, setName] = React.useState<string>('');

  const saveButtonClick = async () => {
    try {
      setStatus(Status.Saving);

      if (selectedFolderId) {
        const projectService = SkyMapAxiosServiceFactory.instance.createFolderServiceV1();
        await projectService.createSubFolder({
          path: { folderId: selectedFolderId },
          body: { name: name },
        });
      } else {
        const projectService = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
        await projectService.createRootFolder({
          path: { projectId: projectContext.project.id },
          body: { name: name },
        });
      }
      props.onClose(false);
    } catch (err) {
      setStatus(Status.Error);
      handleError(err);
    }
  };

  const cancelButtonClick = () => {
    props.onClose(true);
  };

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      width={350}
      onClose={() => props.onClose(true)}
    >
      {{
        header: t('fileManager.createFolderDialog.title', { ns: 'components' }),
        content: (
          <>
            <Label>
              <span>{t('fileManager.createFolderDialog.nameField', { ns: 'components' })}</span>
              <TextBox
                disabled={status === Status.Saving}
                errorMessage={getErrorForPath('body.name')}
                ref={ref}
                onChange={(e) => {
                  setName(e.target.value);
                  clearErrorForPath('body.name');
                }}
              />
            </Label>
          </>
        ),
        footer: {
          right: (
            <>
              <Button
                color="primary"
                disabled={status === Status.Saving}
                loading={status === Status.Saving}
                variant="contained"
                onClick={saveButtonClick}
              >
                {t('create', { ns: 'common' })}
              </Button>
              <Button
                disabled={status === Status.Saving}
                variant="text"
                onClick={cancelButtonClick}
              >
                {t('cancel', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};

const Label = styled.label`
  span {
    display: block;
    margin-bottom: 0.4em;
    font-weight: 300;
    font-size: 0.9em;
  }
`;

export { CreateFolderDialog };
